import { Fragment, useState } from 'react';
import { Menu, Transition } from '@headlessui/react';
import { Link } from 'react-router-dom';
import { X, Menu as MenuIcon, ChevronDown } from 'react-feather';
import clsx from 'clsx';
import { Logo } from './Logo';

import { paymentSolutions, partnerships, resources } from './navLinks';

export function Navbar() {
  const [mobileOpen, setMobileOpen] = useState(false);
  const [openCategory, setOpenCategory] = useState(null); // for Payment Solutions categories
  const [openSection, setOpenSection] = useState(null); // for top-level sections on mobile

  const toggleSection = (section) => {
    setOpenSection(openSection === section ? null : section);
    setOpenCategory(null); // reset nested when changing section
  };

  const toggleCategory = (category) => {
    setOpenCategory(openCategory === category ? null : category);
  };

  return (
    <nav className="relative z-30 bg-transparent text-white">
      <div className="flex justify-between items-center mx-6 md:mx-24 py-6">
        <Logo />

        {/* Desktop Navigation (small dropdowns) */}
        <div className="hidden md:flex space-x-8 items-center relative">
          {/* Payment Solutions */}
          <Menu as="div" className="relative">
            {({ open }) => (
              <>
                <Menu.Button className="hover:text-[#F7931E]">Payment Solutions</Menu.Button>
                <Transition
                  as={Fragment}
                  show={open}
                  enter="transition ease-out duration-200"
                  enterFrom="transform opacity-0 translate-y-2"
                  enterTo="transform opacity-100 translate-y-0"
                  leave="transition ease-in duration-150"
                  leaveFrom="transform opacity-100 translate-y-0"
                  leaveTo="transform opacity-0 translate-y-2"
                >
                  <Menu.Items
                    static
                    className="absolute right-0 mt-3 w-[420px] max-h-[70vh] overflow-y-auto bg-[#0d0d0d] border border-[#F7931E]/20 rounded-lg shadow-xl p-4 space-y-6 z-50"
                  >
                    {paymentSolutions.map((cat) => (
                      <div key={cat.title}>
                        <h4 className="text-[#F7931E] font-bold mb-3">{cat.title}</h4>
                        <div className="space-y-3">
                          {cat.items.map((item) => (
                            <Link
                              key={item.label}
                              to={item.link}
                              className="flex flex-col p-3 bg-[#111]/60 rounded-lg border border-[#F7931E]/20 hover:shadow-lg hover:scale-[1.02] transition-transform duration-200"
                            >
                              <div className="flex items-center gap-2">
                                <item.icon size={20} className="text-[#F7931E]" />
                                <span className="font-semibold text-white">{item.label}</span>
                              </div>
                              <p className="mt-1 text-xs text-gray-400">{item.desc}</p>
                            </Link>
                          ))}
                        </div>
                      </div>
                    ))}
                  </Menu.Items>
                </Transition>
              </>
            )}
          </Menu>

          {/* Partnerships */}
          <Menu as="div" className="relative">
            {({ open }) => (
              <>
                <Menu.Button className="hover:text-[#F7931E]">Partnerships</Menu.Button>
                <Transition
                  as={Fragment}
                  show={open}
                  enter="transition ease-out duration-200"
                  enterFrom="transform opacity-0 translate-y-2"
                  enterTo="transform opacity-100 translate-y-0"
                  leave="transition ease-in duration-150"
                  leaveFrom="transform opacity-100 translate-y-0"
                  leaveTo="transform opacity-0 translate-y-2"
                >
                  <Menu.Items
                    static
                    className="absolute right-0 mt-3 w-[300px] bg-[#0d0d0d] border border-[#F7931E]/20 rounded-lg shadow-xl p-4 space-y-2 z-50"
                  >
                    {partnerships.map((p) => (
                      <Link
                        key={p.label}
                        to={p.link}
                        className="flex items-center gap-3 p-2 rounded-md hover:bg-[#111]/60 hover:text-[#F7931E] transition"
                      >
                        <p.icon size={18} className="text-[#F7931E]" />
                        {p.label}
                      </Link>
                    ))}
                  </Menu.Items>
                </Transition>
              </>
            )}
          </Menu>

          {/* Pricing */}
          <Link to="/pricing" className="hover:text-[#F7931E]">
            Pricing
          </Link>

          {/* Resources */}
          <Menu as="div" className="relative">
            {({ open }) => (
              <>
                <Menu.Button className="hover:text-[#F7931E]">Resources</Menu.Button>
                <Transition
                  as={Fragment}
                  show={open}
                  enter="transition ease-out duration-200"
                  enterFrom="transform opacity-0 translate-y-2"
                  enterTo="transform opacity-100 translate-y-0"
                  leave="transition ease-in duration-150"
                  leaveFrom="transform opacity-100 translate-y-0"
                  leaveTo="transform opacity-0 translate-y-2"
                >
                  <Menu.Items
                    static
                    className="absolute right-0 mt-3 w-[300px] bg-[#0d0d0d] border border-[#F7931E]/20 rounded-lg shadow-xl p-4 space-y-2 z-50"
                  >
                    {resources.map((r) => (
                      <Link
                        key={r.label}
                        to={r.link}
                        className="flex items-center gap-3 p-2 rounded-md hover:bg-[#111]/60 hover:text-[#F7931E] transition"
                      >
                        <r.icon size={18} className="text-[#F7931E]" />
                        {r.label}
                      </Link>
                    ))}
                  </Menu.Items>
                </Transition>
              </>
            )}
          </Menu>
        </div>

        {/* Mobile Hamburger */}
        <div className="md:hidden">
          <button onClick={() => setMobileOpen(!mobileOpen)} aria-label="Toggle Menu">
            {mobileOpen ? <X size={28} /> : <MenuIcon size={28} />}
          </button>
        </div>
      </div>

      {/* Mobile Drawer */}
      <Transition
        show={mobileOpen}
        as={Fragment}
        enter="transition duration-300 ease-out"
        enterFrom="translate-x-full"
        enterTo="translate-x-0"
        leave="transition duration-300 ease-in"
        leaveFrom="translate-x-0"
        leaveTo="translate-x-full"
      >
        <div className="fixed inset-y-0 right-0 w-3/4 bg-[#0d0d0d] text-gray-300 z-50 p-6 flex flex-col space-y-4 shadow-lg overflow-y-auto">
          <button className="self-end mb-6" onClick={() => setMobileOpen(false)}>
            <X size={28} />
          </button>

          {/* Accordion for Payment Solutions */}
          <div>
            <button
              onClick={() => toggleSection('Payment Solutions')}
              className="flex justify-between w-full text-left text-lg font-semibold text-white hover:text-[#F7931E] py-2"
            >
              Payment Solutions
              <ChevronDown
                size={18}
                className={clsx(
                  'transition-transform',
                  openSection === 'Payment Solutions' ? 'rotate-180' : ''
                )}
              />
            </button>
            {openSection === 'Payment Solutions' && (
              <div className="mt-2 pl-2 space-y-4">
                {paymentSolutions.map((cat) => (
                  <div key={cat.title} className="border-t border-gray-700 pt-3">
                    <button
                      onClick={() => toggleCategory(cat.title)}
                      className="flex justify-between w-full text-left text-sm font-semibold text-gray-300 hover:text-[#F7931E] py-1"
                    >
                      {cat.title}
                      <ChevronDown
                        size={14}
                        className={clsx(
                          'transition-transform',
                          openCategory === cat.title ? 'rotate-180' : ''
                        )}
                      />
                    </button>
                    {openCategory === cat.title && (
                      <div className="pl-4 mt-2 space-y-3">
                        {cat.items.map((item) => (
                          <Link
                            key={item.label}
                            to={item.link}
                            className="block p-3 bg-[#111]/60 rounded-lg border border-[#F7931E]/20 shadow hover:shadow-lg hover:scale-[1.02] transition-transform duration-200"
                          >
                            <item.icon size={20} className="text-[#F7931E]" />
                            <h5 className="mt-1 font-bold text-white">{item.label}</h5>
                            <p className="mt-1 text-xs text-gray-400">{item.desc}</p>
                          </Link>
                        ))}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Accordion for Partnerships */}
          <div>
            <button
              onClick={() => toggleSection('Partnerships')}
              className="flex justify-between w-full text-left text-lg font-semibold text-white hover:text-[#F7931E] py-2"
            >
              Partnerships
              <ChevronDown
                size={18}
                className={clsx(
                  'transition-transform',
                  openSection === 'Partnerships' ? 'rotate-180' : ''
                )}
              />
            </button>
            {openSection === 'Partnerships' && (
              <div className="pl-4 mt-2 space-y-3">
                {partnerships.map((p) => (
                  <Link
                    key={p.label}
                    to={p.link}
                    className="flex items-center gap-3 p-2 rounded-md hover:bg-[#111]/60 hover:text-[#F7931E] transition"
                  >
                    <p.icon size={18} className="text-[#F7931E]" />
                    {p.label}
                  </Link>
                ))}
              </div>
            )}
          </div>

          {/* Accordion for Resources */}
          <div>
            <button
              onClick={() => toggleSection('Resources')}
              className="flex justify-between w-full text-left text-lg font-semibold text-white hover:text-[#F7931E] py-2"
            >
              Resources
              <ChevronDown
                size={18}
                className={clsx(
                  'transition-transform',
                  openSection === 'Resources' ? 'rotate-180' : ''
                )}
              />
            </button>
            {openSection === 'Resources' && (
              <div className="pl-4 mt-2 space-y-3">
                {resources.map((r) => (
                  <Link
                    key={r.label}
                    to={r.link}
                    className="flex items-center gap-3 p-2 rounded-md hover:bg-[#111]/60 hover:text-[#F7931E] transition"
                  >
                    <r.icon size={18} className="text-[#F7931E]" />
                    {r.label}
                  </Link>
                ))}
              </div>
            )}
          </div>

          {/* Pricing (always visible) */}
          <Link to="/pricing" className="text-lg font-semibold hover:text-[#F7931E]">
            Pricing
          </Link>
        </div>
      </Transition>
    </nav>
  );
}
